/**
 * LoginModal — native Modal primitive (no the legacy component).
 *
 * Per `the no-legacy-components rule`: new modal wiring uses
 * `@/widgets/Modal` instead of `the legacy modal`. The visual chrome
 * (header bar, body, close button) is plain markup styled by
 * `style/modals/_magic-login-modal.scss`, which now scopes under
 * the bare `.magic-login-modal` class (no `.legacy-styling`
 * compound selector required).
 *
 * Behavior preserved from the prior the legacy modal version:
 *  - dismissal is gated by an explicit close button (the X). Clicking
 *    the scrim does NOT close (login flow requires the user finish or
 *    explicitly bail — accidental closes invalidate the magic-link code).
 *  - invite paths still render the banner background + shader.
 *  - logo selection branches by Overwolf / invite / default.
 */

import { FC, useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { useSearchParams } from "react-router";
import { isMobile } from "react-device-detect";
import Modal from "@/widgets/Modal";
import ThirdwebLogin from "@/pages/ThirdwebLogin";
import { useGetGuild } from "@/hooks/useGetGuild";
import useThemePreference from "@/hooks/useThemePreference";
import { useGetAsset } from "@/hooks/useAsset";
import { InvitedWelcomeModal } from "../InvitedWelcomeModal";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";

interface props {
    isOpen: boolean;
    onClose: () => void;
    forceDarkMode?: boolean;
}

const LoginModal: FC<props> = ({ isOpen, onClose, forceDarkMode }) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const referralCode = useSelector((store: IStore) => store.affiliateStore.referralCode);
    const [showInvitedModal, setShowInvitedModal] = useState(false);
    const { isOnDarkMode } = useThemePreference();
    const [searchParams] = useSearchParams();
    const guildInviteId = searchParams.get("guildId");
    const challengeInviteID = searchParams.get("challengeId");
    const {data: guildInfo} = useGetGuild(guildInviteId);
    const {data: challengeInfo} = useGetChallenge(challengeInviteID);
    const guildAssetImage = useGetAsset(guildInfo?.hero);
    const challengeAssetImage = useGetAsset(challengeInfo?.backgroundImageSmall);
    const isInvite = !!referralCode;
    const isGuildInvite = isInvite && guildInviteId;
    const isChallengeInvite = isInvite && challengeInviteID;

    useEffect(() => {
        if (isOpen && fullToken) {
            onClose();
        }
    }, [fullToken?.id, isOpen]);

    const getBannerImage = () => {
        if (isGuildInvite) {
            return guildAssetImage?.data?.cdnUrl;
        }
        if (isChallengeInvite) {
            return challengeAssetImage?.data?.cdnUrl;
        }
        return `${import.meta.env.VITE_CDN_URL}/images/referral-modal-${isMobile ? "sm" : "lg"}.webp`;
    };

    const getLogo = () => {
        if (isChallengeInvite) {
            return null;
        }
        if (isOnOverwolf) {
            return <img
                src={`${import.meta.env.VITE_CDN_URL}/images/DevourPlayLogoWhite.webp`}
                alt="DevourPlay"
                className="overwolf-landing-page_login-logo"
            />;
        }
        if (isInvite) {
            return <img
                src={`${import.meta.env.VITE_CDN_URL}/images/devour-logo-white.png`}
                alt="DevourPlay"
                style={{height: "auto", width: "6rem"}}
            />;
        }
        return <img
            src="/images/devourplay-logo.png"
            alt="DevourPlay"
            style={{width: "200px", height: "auto"}}
        />;
    };

    const handleInvitedRegistered = () => {
        if (isInvite && !isChallengeInvite) {
            setShowInvitedModal(true);
        }
    };

    /*
     * Skip rendering when the user is already logged in and the modal
     * is closed — avoids the InvitedWelcomeModal flash on background nav.
     */
    if (fullToken && !isOpen && !showInvitedModal) {
        return null;
    }

    return (
        <>
            <InvitedWelcomeModal
                isVisible={showInvitedModal}
                onClose={() => setShowInvitedModal(false)}
            />
            <Modal
                isOpen={isOpen}
                /*
                 * Accidental scrim dismiss invalidates the magic-link code,
                 * so dismissal is gated to the close button only.
                 */
                disableOverlayDismiss
                size={isOnOverwolf ? "xs2" : "sm2"}
                containerClassName="magic-login-modal-container"
                contentClassName={classNames("magic-login-modal", {
                    "is-invite": !!referralCode,
                })}
                ariaLabel="Sign in to DevourPlay"
            >
                {isInvite &&
                    <>
                        <div
                            className="invite-modal_background"
                            style={{
                                backgroundImage: `url(${getBannerImage()})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                height: "8.73556rem",
                            }}
                        />
                        <div
                            className="invite-modal_shader"
                            style={{
                                background: `linear-gradient(180deg, rgba(45, 45, 47, 0.10) 0%, ${
                                    isOnDarkMode || forceDarkMode ? "#2D2D2F" : "white"
                                } 7rem)`,
                            }}
                        />
                    </>
                }

                <header className="magic-login-modal__header">
                    <div className="magic-login-modal__header-logo">
                        {getLogo()}
                    </div>
                    <button
                        type="button"
                        className="magic-login-modal__close"
                        onClick={onClose}
                        aria-label="Close login"
                    >
                        <FaTimes />
                    </button>
                </header>

                <div className="magic-login-modal__body">
                    <ThirdwebLogin
                        isOnDialog={true}
                        onInvitedRegistered={handleInvitedRegistered}
                    />
                </div>
            </Modal>
        </>
    );
};

export default LoginModal;
